import { type RoleUpdateApprovalRequest } from '@column/api';

import {
  BankAccountLevelPermissionsToLabelsMap,
  EntityLevelPermissionsToLabelsMap,
  PlatformLevelPermissionsToLabelsMap,
} from '~/lib/roles/utils';
import { CustomRoleResponseObject } from '~/typings/API';

import { type AccountOwners, type FieldChange, type OverrideChange, type PermissionDiff } from './types';

type PermissionMap = Record<string, unknown>;
type OverrideIdKey = 'entityId' | 'bankAccountId';
type ParentResolver = (id: string, key: string, side: 'from' | 'to') => string | undefined;

const extraPermissionLabels: Record<string, string> = {
  apiKeys: 'API Keys',
  counterparties: 'Counterparties',
  loans: 'Loans',
  platformInfo: 'Platform Info',
  reporting: 'Reporting',
  transferTemplates: 'Transfer Templates',
  users: 'Users',
  webhooks: 'Webhooks',
};

const accessLabels: Record<string, string> = {
  default: 'Default',
  none: 'No Access',
  read: 'View Only',
  update: 'Edit Access',
  write: 'Full Access',
};

const TRANSFER_KEYS = new Set([
  'achCredits',
  'achDebits',
  'achReturns',
  'wireTransfers',
  'bookTransfers',
  'realtimeTransfers',
  'issueChecks',
  'depositChecks',
  'stopPayments',
]);

const entityLabels = EntityLevelPermissionsToLabelsMap as Record<string, string | undefined>;
const bankAccountLabels = BankAccountLevelPermissionsToLabelsMap as Record<string, string | undefined>;

const toCamel = (key: string) => key.replace(/_(\w)/g, (_, c: string) => c.toUpperCase());

const humanize = (key: string) => key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const camelizeKeys = (value: unknown): PermissionMap =>
  Object.fromEntries(Object.entries((value as PermissionMap) ?? {}).map(([key, v]) => [toCamel(key), v]));

const permissionLabel = (key: string) => {
  const camel = toCamel(key);
  return (
    (PlatformLevelPermissionsToLabelsMap as Record<string, string | undefined>)[camel] ??
    extraPermissionLabels[camel] ??
    humanize(key)
  );
};

const formatAccess = (level?: string) => accessLabels[level || 'default'] ?? humanize(level || 'default');

const isDefault = (level?: unknown) => !level || level === 'default';

const formatWithParent = (level: unknown, parent?: string) =>
  formatAccess((isDefault(level) ? (parent && !isDefault(parent) ? parent : 'none') : level) as string);

const getPermissionChanges = (
  from: Record<string, string | undefined> = {},
  to: Record<string, string | undefined> = {}
): FieldChange[] =>
  Array.from(new Set([...Object.keys(from), ...Object.keys(to)]))
    .map((key) => ({
      key: toCamel(key),
      label: permissionLabel(key),
      from: formatWithParent(from[key]),
      to: formatWithParent(to[key]),
    }))
    .filter((change) => change.from !== change.to);

export const collapseTransferChanges = (changes: FieldChange[]): FieldChange[] => {
  const transfers = changes.filter((change) => change.key && TRANSFER_KEYS.has(change.key));
  if (transfers.length < 2 || transfers.some((c) => c.from !== transfers[0].from || c.to !== transfers[0].to)) {
    return changes;
  }
  const index = changes.indexOf(transfers[0]);
  const rest = changes.filter((change) => !transfers.includes(change));
  rest.splice(index, 0, { key: 'transfers', label: 'Transfers', from: transfers[0].from, to: transfers[0].to });
  return rest;
};

export const getOverrideIds = (list: unknown[] | null | undefined, idKey: OverrideIdKey) =>
  (list ?? [])
    .map(camelizeKeys)
    .map((o) => String(o[idKey] ?? ''))
    .filter(Boolean);

const diffOverrides = (
  from: unknown[] | null | undefined,
  to: unknown[] | null | undefined,
  idKey: OverrideIdKey,
  labels: Record<string, string | undefined>,
  parent: ParentResolver
): OverrideChange[] => {
  const index = (list: unknown[] | null | undefined) =>
    new Map((list ?? []).map(camelizeKeys).map((o) => [String(o[idKey] ?? ''), o] as const));
  const before = index(from);
  const after = index(to);
  return Array.from(new Set([...before.keys(), ...after.keys()]))
    .filter(Boolean)
    .map((id) => {
      const prev = before.get(id) ?? {};
      const next = after.get(id) ?? {};
      const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)])).filter((k) => k !== idKey);
      const changes = keys
        .map((key) => ({
          key,
          label: labels[key] ?? permissionLabel(key),
          from: formatWithParent(prev[key], parent(id, key, 'from')),
          to: formatWithParent(next[key], parent(id, key, 'to')),
        }))
        .filter((change) => change.from !== change.to);
      const kind: OverrideChange['kind'] = !before.has(id) ? 'added' : !after.has(id) ? 'removed' : 'changed';
      return { id, kind, changes };
    })
    .filter((change) => change.changes.length > 0);
};

interface RoleSide {
  platform: PermissionMap;
  entities: Map<string, PermissionMap>;
}

type RoleSides = Record<'from' | 'to', RoleSide>;

const roleSide = (platform: unknown, entityOverrides: unknown[] | null | undefined): RoleSide => ({
  platform: camelizeKeys(platform),
  entities: new Map((entityOverrides ?? []).map(camelizeKeys).map((o) => [String(o.entityId ?? ''), o] as const)),
});

const entityParent =
  (sides: RoleSides): ParentResolver =>
  (_id, key, side) =>
    sides[side].platform[key === 'thisEntity' ? 'entities' : key] as string | undefined;

const bankAccountParent =
  (sides: RoleSides, accountOwners: AccountOwners): ParentResolver =>
  (id, key, side) => {
    const parentKey = key === 'thisBankAccount' ? 'bankAccounts' : key;
    const owner = (accountOwners.get(id) ?? []).find((entityId) => sides[side].entities.has(entityId));
    const entityLevel = owner ? sides[side].entities.get(owner)?.[parentKey] : undefined;
    return (isDefault(entityLevel) ? sides[side].platform[parentKey] : entityLevel) as string | undefined;
  };

export const getRoleDiff = (
  from: CustomRoleResponseObject | undefined,
  to: CustomRoleResponseObject | undefined,
  accountOwners: AccountOwners
): PermissionDiff => {
  const sides = {
    from: roleSide(from?.platformLevelPermissions, from?.entityLevelOverrides),
    to: roleSide(to?.platformLevelPermissions, to?.entityLevelOverrides),
  };
  return {
    details: [],
    platform: getPermissionChanges(
      from?.platformLevelPermissions as Record<string, string | undefined>,
      to?.platformLevelPermissions as Record<string, string | undefined>
    ),
    entities: diffOverrides(
      from?.entityLevelOverrides,
      to?.entityLevelOverrides,
      'entityId',
      entityLabels,
      entityParent(sides)
    ),
    bankAccounts: diffOverrides(
      from?.bankAccountLevelOverrides,
      to?.bankAccountLevelOverrides,
      'bankAccountId',
      bankAccountLabels,
      bankAccountParent(sides, accountOwners)
    ),
  };
};

export const getRoleUpdateDiff = (
  { update, previous }: RoleUpdateApprovalRequest,
  accountOwners: AccountOwners
): PermissionDiff => {
  const details: FieldChange[] = [];
  if (update.name != null && update.name !== previous.name) {
    details.push({ label: 'Name', from: previous.name ?? '', to: update.name });
  }
  if (update.description != null && update.description !== previous.description) {
    details.push({ label: 'Description', from: previous.description || '—', to: update.description || '—' });
  }
  const prevPlatform: Record<string, string | undefined> = previous.platform_level_permissions ?? {};
  const sides = {
    from: roleSide(previous.platform_level_permissions, previous.entity_level_overrides),
    to: roleSide(
      { ...prevPlatform, ...update.platform_level_permissions },
      update.entity_level_overrides ?? previous.entity_level_overrides
    ),
  };
  return {
    details,
    platform: update.platform_level_permissions
      ? getPermissionChanges(
          Object.fromEntries(Object.keys(update.platform_level_permissions).map((k) => [k, prevPlatform[k]])),
          update.platform_level_permissions
        )
      : [],
    entities: update.entity_level_overrides
      ? diffOverrides(
          previous.entity_level_overrides,
          update.entity_level_overrides,
          'entityId',
          entityLabels,
          entityParent(sides)
        )
      : [],
    bankAccounts: update.bank_account_level_overrides
      ? diffOverrides(
          previous.bank_account_level_overrides,
          update.bank_account_level_overrides,
          'bankAccountId',
          bankAccountLabels,
          bankAccountParent(sides, accountOwners)
        )
      : [],
  };
};
